import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Spin, DatePicker, Button, Space, Table } from 'antd';
import { FilePdfOutlined, FileWordOutlined } from '@ant-design/icons';
import { LineChart, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell, AreaChart, Area, LabelList, ReferenceLine } from 'recharts';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getDsmPenalty, getMarketDashboard, getVolumeDashboard, getRevenueAnalysis, getMarketSnapshot, getGDAMAllocationAnalysis } from '../services/api';
import logoImg from '../assets/logo.png';
import { exportWeeklyReportDocx } from '../utils/reportDocxExporter';

const { Title, Text } = Typography;

const IndicatorTooltipWeekly = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const row = payload[0].payload;
    if (row.alignment_indicator === 1) {
      return (
        <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0', padding: '6px 10px', fontSize: 10, borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ color: '#64748b', marginBottom: 2 }}>Date: {label}</div>
          <div style={{ fontWeight: 600, color: '#1f2937' }}>Segment: {row.alignment_segment}</div>
        </div>
      );
    }
  }
  return null;
};

// Color palette
const COLORS = {
  primary: '#0052CC', // Apraava Blue
  secondary: '#E67E22', // Apraava Orange
  gdam: '#27AE60', // GDAM Green
  gdamLight: '#2ECC71', // GDAM Light Green
  dark: '#1e293b', // Slate 800
  light: '#f8fafc', // Slate 50
  border: '#cbd5e1', // Border Grey
};

export default function WeeklyReportPage() {
  const defaultStartDate = dayjs().subtract(7, 'day');
  const defaultEndDate = dayjs().subtract(1, 'day');
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingWord, setGeneratingWord] = useState(false);
  const [generationTime, setGenerationTime] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [marketSnapshotData, setMarketSnapshotData] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalGen: '—',
    totalSched: '—',
    peakGen: '—',
    lowestGen: '—',
    avgGen: '—',
    plf: '—',
    gdamVol: '—',
    damVol: '—',
    rtmVol: '—',
    bilatVol: '—',
    bilatPct: '—',
    exchangeVol: '—',
    exchangePct: '—',
    gdamTariff: '—',
    damTariff: '—',
    rtmTariff: '—',
    bilatTariff: '—',
    plantTariff: '—',
    gdamGrossTariff: '—',
    damGrossTariff: '—',
    rtmGrossTariff: '—',
    bilatGrossTariff: '—',
    plantGrossTariff: '—',
    mAvgGdam: '—',
    mAvgDam: '—',
    mAvgRtm: '—',
    totalGrossRev: '—',
    netRevenuePreDsm: '—',
    dsmPenalty: '—',
    netRevenuePostDsm: '—',
    dsmPayable: '—',
    dsmReceivable: '—',
    dsmPenaltyVal: '—',
    revenueYtd: '—',
    revenueMtd: '—',
    revenueThisWeek: '—',
    realizationGross: '—',
    realizationBilatGross: '—',
    realizationExchGross: '—',
    overallExchRealisation: '—',
    realizationCharges: '—',
    realizationDsm: '—',
    realizationPostDsm: '—',
    efficiency: '—'
  });

  // Handlers to enforce weekly range (max 7 days) between start and end date
  const handleStartDateChange = (val) => {
    if (!val) return;
    const yesterday = dayjs().subtract(1, 'day');
    let newEnd = val.add(6, 'day');
    if (newEnd.isAfter(yesterday, 'day')) {
      newEnd = yesterday;
    }
    setStartDate(val);
    setEndDate(newEnd);
  };

  const handleEndDateChange = (val) => {
    if (!val) return;
    let newStart = startDate;
    if (val.isBefore(newStart, 'day') || val.diff(newStart, 'day') > 6) {
      newStart = val.subtract(6, 'day');
      setStartDate(newStart);
    }
    setEndDate(val);
  };

  // Fetch week range data
  const fetchWeeklyReportData = async (activeStart = startDate, activeEnd = endDate) => {
    setLoading(true);
    const fromDate = activeStart || startDate;
    const toDate = activeEnd || endDate;
    const dateTo = toDate.format('YYYY-MM-DD');
    const dateFrom = fromDate.format('YYYY-MM-DD');
    const plantId = 1; // Default plant Andra Lake

    try {
      const params = {
        plant_id: plantId,
        date_from: dateFrom,
        date_to: dateTo,
        granularity: 'daily'
      };

      const dsmParams = {
        plant_id: plantId,
        date_from: dateFrom,
        date_to: dateTo,
        granularity: '15min'
      };

      const selectedYear = fromDate.year();
      const selectedMonthNum = fromDate.month() + 1;
      const fyYear = selectedMonthNum >= 4 ? selectedYear : selectedYear - 1;
      const fyStart = `${fyYear}-04-01`;
      const mStart = fromDate.startOf('month').format('YYYY-MM-DD');

      const mtdRevenueParams = {
        plant_id: plantId,
        date_from: mStart,
        date_to: dateTo,
        granularity: 'daily'
      };
      const ytdRevenueParams = {
        plant_id: plantId,
        date_from: fyStart,
        date_to: dateTo,
        granularity: 'monthly'
      };

      const [resDsm, resMarket, resVolume, resRevenue, resSnapshot, resMtdRev, resYtdRev, resAnalysis] = await Promise.all([
        getDsmPenalty(dsmParams).catch(err => {
          console.warn('getDsmPenalty failed, using empty array', err);
          return { data: { chart_data: [] } };
        }),
        getMarketDashboard(params).catch(err => {
          console.warn('getMarketDashboard failed, using empty array', err);
          return { data: { results: [] } };
        }),
        getVolumeDashboard(params).catch(err => {
          console.warn('getVolumeDashboard failed, using empty array', err);
          return { data: { results: [] } };
        }),
        getRevenueAnalysis(params).catch(err => {
          console.warn('getRevenueAnalysis failed, using empty array', err);
          return { data: { results: [] } };
        }),
        getMarketSnapshot({ date: dateTo }).catch(err => {
          console.warn('getMarketSnapshot failed, using empty array', err);
          return { data: { results: [] } };
        }),
        getRevenueAnalysis(mtdRevenueParams).catch(err => {
          console.warn('getRevenueAnalysis MTD failed', err);
          return { data: { results: [] } };
        }),
        getRevenueAnalysis(ytdRevenueParams).catch(err => {
          console.warn('getRevenueAnalysis YTD failed', err);
          return { data: { results: [] } };
        }),
        getGDAMAllocationAnalysis({
          plant_id: plantId,
          start_date: dateFrom,
          end_date: dateTo,
          what_if_rules: []
        }).catch(err => {
          console.warn('getGDAMAllocationAnalysis failed, using empty object', err);
          return { data: { daily_situations: {} } };
        })
      ]);

      const dsmList = resDsm.data?.chart_data || [];
      const marketList = resMarket.data?.results || [];
      const volumeList = resVolume.data?.results || [];
      const revenueList = resRevenue.data?.results || [];
      const snapshotList = resSnapshot.data?.results || [];

      setMarketSnapshotData(snapshotList);

      // If APIs return empty, set clean zero/dash state without any mock data
      if (dsmList.length === 0 && marketList.length === 0 && volumeList.length === 0 && revenueList.length === 0) {
        console.info('No backend data available for selected weekly range.');
        setChartData([]);
        setSummaryMetrics({
          totalGen: '0.00',
          totalSched: '0.00',
          peakGen: '0.00',
          lowestGen: '0.00',
          avgGen: '0.00',
          plf: '0.00',
          gdamVol: '0.00',
          damVol: '0.00',
          rtmVol: '0.00',
          bilatVol: '0.00',
          bilatPct: '0.0%',
          exchangeVol: '0.00',
          exchangePct: '0.0%',
          gdamTariff: '0.00',
          damTariff: '0.00',
          rtmTariff: '0.00',
          bilatTariff: '0.00',
          plantTariff: '0.00',
          gdamGrossTariff: '0.00',
          damGrossTariff: '0.00',
          rtmGrossTariff: '0.00',
          bilatGrossTariff: '0.00',
          plantGrossTariff: '0.00',
          mAvgGdam: '0.00',
          mAvgDam: '0.00',
          mAvgRtm: '0.00',
          totalGrossRev: '0.00',
          netRevenuePreDsm: '0.00',
          dsmPenalty: '0.00',
          netRevenuePostDsm: '0.00',
          dsmPayable: '0.00',
          dsmReceivable: '0.00',
          dsmPenaltyVal: '0.00',
          revenueYtd: '0.00',
          revenueMtd: '0.00',
          revenueThisWeek: '0.00',
          realizationGross: '0.00',
          realizationBilatGross: '0.00',
          realizationExchGross: '0.00',
          overallExchRealisation: '0.00',
          realizationCharges: '0.00',
          realizationDsm: '0.00',
          realizationPostDsm: '0.00',
          efficiency: '—'
        });
      } else {
        // Initialize merged map with target dates in range
        const mergedMap = {};
        const daysDiff = toDate.diff(fromDate, 'day');
        for (let i = 0; i <= daysDiff; i++) {
          const currentDate = fromDate.add(i, 'day');
          const dateStr = currentDate.format('YYYY-MM-DD');
          mergedMap[dateStr] = {
            period: dateStr,
            time: currentDate.format('DD-MMM'),
            actual: 0,
            schedule: 0,
            gdam_mcp: 0,
            dam_mcp: 0,
            rtm_mcp: 0,
            gdam_mcp_kwh: 0,
            dam_mcp_kwh: 0,
            rtm_mcp_kwh: 0,
            gdam_cleared: 0,
            dam_cleared: 0,
            rtm_cleared: 0,
            oi_20_plus: 0,
            oi_15_20: 0,
            oi_12_15: 0,
            oi_0_12: 0,
            ui_0_12: 0,
            ui_12_15: 0,
            ui_15_20: 0,
            ui_20_plus: 0
          };
        }

        // Sum DSM actual and schedule (converting block MW values to daily averages)
        dsmList.forEach((row) => {
          if (!row.period) return;
          const cleanPeriod = row.period.split(' ')[0].trim();
          const dStr = dayjs(cleanPeriod).format('YYYY-MM-DD');
          if (mergedMap[dStr]) {
            mergedMap[dStr].actual += (Number(row.actual) || 0) / 96.0;
            mergedMap[dStr].schedule += (Number(row.schedule) || 0) / 96.0;
            mergedMap[dStr].oi_20_plus += Number(row.oi_20_plus) || 0;
            mergedMap[dStr].oi_15_20 += Number(row.oi_15_20) || 0;
            mergedMap[dStr].oi_12_15 += Number(row.oi_12_15) || 0;
            mergedMap[dStr].oi_0_12 += Number(row.oi_0_12) || 0;
            mergedMap[dStr].ui_0_12 += Number(row.ui_0_12) || 0;
            mergedMap[dStr].ui_12_15 += Number(row.ui_12_15) || 0;
            mergedMap[dStr].ui_15_20 += Number(row.ui_15_20) || 0;
            mergedMap[dStr].ui_20_plus += Number(row.ui_20_plus) || 0;
          }
        });

        // Average market prices
        const priceCounts = {};
        marketList.forEach((row) => {
          if (!row.period) return;
          const dStr = dayjs(row.period).format('YYYY-MM-DD');
          if (mergedMap[dStr]) {
            mergedMap[dStr].gdam_mcp += (Number(row.gdam_mcp) || 0) * 1000;
            mergedMap[dStr].dam_mcp += (Number(row.dam_mcp) || 0) * 1000;
            mergedMap[dStr].rtm_mcp += (Number(row.rtm_mcp) || 0) * 1000;
            priceCounts[dStr] = (priceCounts[dStr] || 0) + 1;
          }
        });

        Object.keys(priceCounts).forEach(dStr => {
          if (mergedMap[dStr]) {
            mergedMap[dStr].gdam_mcp /= priceCounts[dStr];
            mergedMap[dStr].dam_mcp /= priceCounts[dStr];
            mergedMap[dStr].rtm_mcp /= priceCounts[dStr];
            mergedMap[dStr].gdam_mcp_kwh = mergedMap[dStr].gdam_mcp / 1000.0;
            mergedMap[dStr].dam_mcp_kwh = mergedMap[dStr].dam_mcp / 1000.0;
            mergedMap[dStr].rtm_mcp_kwh = mergedMap[dStr].rtm_mcp / 1000.0;
          }
        });

        // Build volume map and sum cleared volumes
        const volMap = {};
        volumeList.forEach((row) => {
          if (!row.period) return;
          const cleanPeriod = row.period.split(' - ')[0].trim();
          volMap[cleanPeriod] = row;
          const dStr = dayjs(cleanPeriod).format('YYYY-MM-DD');
          if (mergedMap[dStr]) {
            mergedMap[dStr].gdam_cleared += (Number(row.gdam) || 0) / 250.0;
            mergedMap[dStr].dam_cleared += (Number(row.dam) || 0) / 250.0;
            mergedMap[dStr].rtm_cleared += (Number(row.rtm) || 0) / 250.0;
          }
        });

        // Compute real daily exchange gross realisation
        const dailyRealisationMap = {};
        revenueList.forEach(row => {
          if (!row.period) return;
          const cleanPeriod = row.period.split(' - ')[0].trim();
          const dStr = dayjs(cleanPeriod).format('YYYY-MM-DD');
          const volRow = volMap[cleanPeriod] || {};
          const exchVol = (Number(volRow.gdam) || 0) + (Number(volRow.dam) || 0) + (Number(volRow.rtm) || 0);
          const exchGross = (Number(row.gdam_gross_rev) || 0) + (Number(row.dam_gross_rev) || 0) + (Number(row.rtm_gross_rev) || 0);
          if (exchVol > 0 && exchGross > 0) {
            dailyRealisationMap[dStr] = Number((exchGross / exchVol).toFixed(2));
          } else if (Number(row.exchange_gross_rev) > 0 && exchVol > 0) {
            dailyRealisationMap[dStr] = Number((Number(row.exchange_gross_rev) / exchVol).toFixed(2));
          } else if (Number(row.gross_rev) > 0 && Number(row.total_schedule_mw) > 0) {
            dailyRealisationMap[dStr] = Number((Number(row.gross_rev) / (Number(row.total_schedule_mw) * 250.0)).toFixed(2));
          } else {
            dailyRealisationMap[dStr] = 0;
          }
        });

        // Daily Ideal Scenario Electricity Realisation Mapping (from scenario analysis)
        const dailySituations = resAnalysis?.data?.daily_situations || {};
        const dailyIdealRealisationMap = {};
        Object.entries(dailySituations).forEach(([dKey, sitObj]) => {
          const ideal = sitObj?.Ideal;
          if (ideal) {
            const elec = Number(ideal.Electricity || 0);
            const sched = Number(ideal['Scheduled kWh'] || ideal['Ideal Scheduled kWh'] || ideal['Actual Downscaled kWh'] || 0);
            if (sched > 0 && elec > 0) {
              dailyIdealRealisationMap[dKey] = Number((elec / sched).toFixed(2));
            }
          }
        });

        // Compute daily Net Revenue Post DSM efficiency percentages for the week
        const todayStr = dayjs().format('YYYY-MM-DD');
        const weeklyPcts = [];
        Object.entries(dailySituations).forEach(([dKey, sitObj]) => {
          if (dKey >= dateFrom && dKey <= dateTo && dKey <= todayStr) {
            const actPost = Number(sitObj?.Actual?.['Net Revenue Post DSM'] ?? (Number(sitObj?.Actual?.['Gross Revenue'] || 0) - Number(sitObj?.Actual?.['Charges & Commission'] || 0) - Number(sitObj?.Actual?.['DSM Penalty'] || 0)));
            const idealPost = Number(sitObj?.Ideal?.['Net Revenue Post DSM'] ?? (Number(sitObj?.Ideal?.['Gross Revenue'] || 0) - Number(sitObj?.Ideal?.['Charges & Commission'] || 0) - Number(sitObj?.Ideal?.['DSM Penalty'] || 0)));
            if (idealPost > 0 && !isNaN(actPost)) {
              weeklyPcts.push((actPost / idealPost) * 100);
            }
          }
        });
        const weeklyEfficiency = weeklyPcts.length > 0
          ? (weeklyPcts.reduce((sum, v) => sum + v, 0) / weeklyPcts.length).toFixed(1)
          : '—';

        const alignedList = Object.keys(mergedMap).sort().map((dStr, idx) => {
          const rowData = mergedMap[dStr];
          const dayRealisation = dailyRealisationMap[dStr] !== undefined
            ? dailyRealisationMap[dStr]
            : 0;
          const dayIdealRealisation = dailyIdealRealisationMap[dStr] !== undefined
            ? dailyIdealRealisationMap[dStr]
            : null;

          const gdamMcp = Number(rowData.gdam_mcp_kwh) || 0;
          const damMcp = Number(rowData.dam_mcp_kwh) || 0;
          const rtmMcp = Number(rowData.rtm_mcp_kwh) || 0;

          const gdamVol = Number(rowData.gdam_cleared) || 0;
          const damVol = Number(rowData.dam_cleared) || 0;
          const rtmVol = Number(rowData.rtm_cleared) || 0;

          let indicator = 0;
          let alignmentSegment = '';
          let maxPriceSeg = '';
          let maxVolSeg = '';

          if (gdamVol > 0 || damVol > 0 || rtmVol > 0) {
            let maxPrice = -1;
            if (gdamMcp > maxPrice) { maxPrice = gdamMcp; maxPriceSeg = 'GDAM'; }
            if (damMcp > maxPrice) { maxPrice = damMcp; maxPriceSeg = 'DAM'; }
            if (rtmMcp > maxPrice) { maxPrice = rtmMcp; maxPriceSeg = 'RTM'; }

            let maxVol = -1;
            if (gdamVol > maxVol) { maxVol = gdamVol; maxVolSeg = 'GDAM'; }
            if (damVol > maxVol) { maxVol = damVol; maxVolSeg = 'DAM'; }
            if (rtmVol > maxVol) { maxVol = rtmVol; maxVolSeg = 'RTM'; }

            if (maxPriceSeg === maxVolSeg && maxPriceSeg !== '') {
              indicator = 1;
              alignmentSegment = maxPriceSeg;
            }
          }

          return {
            ...rowData,
            block: idx + 1,
            exchange_gross_realisation: dayRealisation,
            ideal_gross_realisation: dayIdealRealisation,
            alignment_indicator: indicator,
            alignment_segment: alignmentSegment,
            max_price_seg: maxPriceSeg,
            max_vol_seg: maxVolSeg
          };
        });

        setChartData(alignedList);

        // Sum summary aggregates
        let sumActual = 0;
        let sumSchedule = 0;
        let maxActual = -Infinity;
        let minActual = Infinity;
        let sumReceivable = 0;
        let sumPayable = 0;
        let sumPenalty = 0;
        dsmList.forEach(row => {
          const act = Number(row.actual) || 0;
          const sched = Number(row.schedule) || 0;
          sumActual += act;
          sumSchedule += sched;
          sumReceivable += Math.abs(Number(row.receivable)) || 0;
          sumPayable += Math.abs(Number(row.payable)) || 0;
          sumPenalty += (Number(row.oi_penalty) || 0) + (Number(row.ui_penalty) || 0);

          const rowPeak = row.peak_actual !== undefined && row.peak_actual !== null ? Number(row.peak_actual) : act;
          const rowLowest = row.lowest_actual !== undefined && row.lowest_actual !== null ? Number(row.lowest_actual) : act;
          if (rowPeak > maxActual) maxActual = rowPeak;
          if (rowLowest < minActual) minActual = rowLowest;
        });

        if (minActual === Infinity) minActual = 0;
        if (maxActual === -Infinity) maxActual = 0;

        let seg_gt_20 = 0, seg_15_20 = 0, seg_12_15 = 0, seg_safe = 0, seg_neg_12_15 = 0, seg_neg_15_20 = 0, seg_lt_neg_20 = 0;
        dsmList.forEach(d => {
          seg_gt_20 += Number(d.oi_20_plus || 0);
          seg_15_20 += Number(d.oi_15_20 || 0);
          seg_12_15 += Number(d.oi_12_15 || 0);
          seg_safe += (Number(d.oi_0_12 || 0) + Math.abs(Number(d.ui_0_12 || 0)));
          seg_neg_12_15 += Math.abs(Number(d.ui_12_15 || 0));
          seg_neg_15_20 += Math.abs(Number(d.ui_15_20 || 0));
          seg_lt_neg_20 += Math.abs(Number(d.ui_20_plus || 0));
        });
        const totalGenVal = (sumActual * 250.0) / 1000000.0;
        const totalGen = totalGenVal.toFixed(2);
        const totalSchedVal = (sumSchedule * 250.0) / 1000000.0;
        const totalSched = totalSchedVal.toFixed(2);

        const numDays = Object.keys(mergedMap).length || 7;
        const peakGen = maxActual.toFixed(2);
        const lowestGen = minActual.toFixed(2);
        const plf = (((sumActual * 250.0) / (106.4 * 1000.0 * 24.0 * numDays)) * 100.0).toFixed(2);
        const avgGen = (sumActual / (numDays * 96.0)).toFixed(2);
        const netDsmImpact = (sumActual > 0 ? (sumPenalty / (sumActual * 250.0)).toFixed(2) : '0.00');

        let sumGdamKwh = 0, sumDamKwh = 0, sumRtmKwh = 0, sumBilatKwh = 0;
        volumeList.forEach(row => {
          sumGdamKwh += Number(row.gdam) || 0;
          sumDamKwh += Number(row.dam) || 0;
          sumRtmKwh += Number(row.rtm) || 0;
          sumBilatKwh += (Number(row.bilat1) || 0) + (Number(row.bilat2) || 0) + (Number(row.bilat3) || 0);
        });

        const gdamVol = (sumGdamKwh / 1000000.0).toFixed(2);
        const damVol = (sumDamKwh / 1000000.0).toFixed(2);
        const rtmVol = (sumRtmKwh / 1000000.0).toFixed(2);
        const bilatVol = (sumBilatKwh / 1000000.0).toFixed(2);

// volMap already initialized above

        let sumGdamGross = 0, sumDamGross = 0, sumRtmGross = 0;
        let sumGdamVolKwh = 0, sumDamVolKwh = 0, sumRtmVolKwh = 0;
        let sumGdamChg = 0, sumDamChg = 0, sumRtmChg = 0;
        let sumBilatNet = 0, sumBilatVolKwh = 0, sumBilatGross = 0;

        revenueList.forEach(row => {
          if (!row.period) return;
          const cleanPeriod = row.period.split(' - ')[0].trim();
          const volRow = volMap[cleanPeriod] || {};

          const gdamVolGen = Number(volRow.gdam) || 0;
          const damVolGen = Number(volRow.dam) || 0;
          const rtmVolGen = Number(volRow.rtm) || 0;
          const totalVolGen = gdamVolGen + damVolGen + rtmVolGen;

          const gdamGross = Number(row.gdam_gross_rev) || 0;
          const damGross = Number(row.dam_gross_rev) || 0;
          const rtmGross = Number(row.rtm_gross_rev) || 0;

          const charges = Number(row.stu_charges_exch) +
            Number(row.nldc_fees_exch) +
            Number(row.trader_comm_exch) +
            Number(row.exch_comm_exch) +
            Number(row.tax_exch);

          sumGdamGross += gdamGross;
          sumDamGross += damGross;
          sumRtmGross += rtmGross;
          sumGdamVolKwh += gdamVolGen;
          sumDamVolKwh += damVolGen;
          sumRtmVolKwh += rtmVolGen;

          if (totalVolGen > 0) {
            sumGdamChg += charges * (gdamVolGen / totalVolGen);
            sumDamChg += charges * (damVolGen / totalVolGen);
            sumRtmChg += charges * (rtmVolGen / totalVolGen);
          }

          sumBilatNet += (Number(row.net_goa) || 0) + (Number(row.net_nidar) || 0) + (Number(row.net_npcl) || 0);
          sumBilatVolKwh += (Number(row.schedule_vol_goa) || 0) + (Number(row.schedule_vol_nidar) || 0) + (Number(row.schedule_vol_npcl) || 0);
          sumBilatGross += Number(row.contract_gross_rev) || ((Number(row.gross_goa) || 0) + (Number(row.gross_nidar) || 0) + (Number(row.gross_npcl) || 0));
        });

        const gdamTariff = sumGdamVolKwh > 0 ? ((sumGdamGross - sumGdamChg) / sumGdamVolKwh).toFixed(2) : '0.00';
        const damTariff = sumDamVolKwh > 0 ? ((sumDamGross - sumDamChg) / sumDamVolKwh).toFixed(2) : '0.00';
        const rtmTariff = sumRtmVolKwh > 0 ? ((sumRtmGross - sumRtmChg) / sumRtmVolKwh).toFixed(2) : '0.00';
        const bilatTariff = sumBilatVolKwh > 0 ? (sumBilatNet / sumBilatVolKwh).toFixed(2) : '0.00';

        const sumExchGross = sumGdamGross + sumDamGross + sumRtmGross;
        const totalExchVolKwh = (sumGdamVolKwh + sumDamVolKwh + sumRtmVolKwh) > 0 ? (sumGdamVolKwh + sumDamVolKwh + sumRtmVolKwh) : (sumGdamKwh + sumDamKwh + sumRtmKwh);
        const totalBilatVolKwh = sumBilatVolKwh > 0 ? sumBilatVolKwh : sumBilatKwh;

        const gdamGrossTariff = sumGdamVolKwh > 0 ? (sumGdamGross / sumGdamVolKwh).toFixed(2) : '0.00';
        const damGrossTariff = sumDamVolKwh > 0 ? (sumDamGross / sumDamVolKwh).toFixed(2) : '0.00';
        const rtmGrossTariff = sumRtmVolKwh > 0 ? (sumRtmGross / sumRtmVolKwh).toFixed(2) : '0.00';
        const bilatGrossTariff = totalBilatVolKwh > 0 ? (sumBilatGross / totalBilatVolKwh).toFixed(2) : '0.00';
        const exchGrossTariff = totalExchVolKwh > 0 ? (sumExchGross / totalExchVolKwh).toFixed(2) : '0.00';

        // Independently calculate volume-weighted exchange realization for the selected date range
        const overallExchRealisation = totalExchVolKwh > 0 ? (sumExchGross / totalExchVolKwh).toFixed(2) : '0.00';

        let sumGrossRev = 0, sumNetPre = 0, sumDsmCharges = 0, sumNetPost = 0;
        revenueList.forEach(row => {
          sumGrossRev += Number(row.gross_rev) || 0;
          sumNetPre += Number(row.net_pre) || 0;
          sumDsmCharges += Number(row.total_dsm_charges) || 0;
          sumNetPost += Number(row.net_post) || 0;
        });

        const totalGrossRev = (sumGrossRev / 1000000.0).toFixed(2);
        const netRevenuePreDsm = (sumNetPre / 1000000.0).toFixed(2);
        const dsmPenalty = (Math.abs(sumDsmCharges) / 1000000.0).toFixed(2);
        const netRevenuePostDsm = (sumNetPost / 1000000.0).toFixed(2);
        const netPlantRealisation = (Number(totalGen) > 0 ? (Number(netRevenuePostDsm) / Number(totalGen)).toFixed(2) : '0.00');

        const kpis = resDsm.data?.kpi_cards || {};
        const dsmPayable = (Number(kpis.net_dsm_payable || 0) / 1000000.0).toFixed(2);
        const dsmReceivable = (Number(kpis.net_dsm_receivable || 0) / 1000000.0).toFixed(2);
        const dsmPenaltyVal = (Number(kpis.dsm_penalty || 0) / 1000000.0).toFixed(2);

        const plantTariff = (Number(totalSched) > 0 ? (Number(netRevenuePreDsm) / Number(totalSched)).toFixed(2) : '0.00');
        const plantGrossTariff = (Number(totalSched) > 0 ? (Number(totalGrossRev) / Number(totalSched)).toFixed(2) : '0.00');

        let sumGdamMcp = 0, sumDamMcp = 0, sumRtmMcp = 0, mcpCount = 0;
        marketList.forEach(row => {
          sumGdamMcp += Number(row.gdam_mcp) || 0;
          sumDamMcp += Number(row.dam_mcp) || 0;
          sumRtmMcp += Number(row.rtm_mcp) || 0;
          mcpCount++;
        });
        const mAvgGdam = mcpCount > 0 ? (sumGdamMcp / mcpCount).toFixed(2) : '0.00';
        const mAvgDam = mcpCount > 0 ? (sumDamMcp / mcpCount).toFixed(2) : '0.00';
        const mAvgRtm = mcpCount > 0 ? (sumRtmMcp / mcpCount).toFixed(2) : '0.00';

        // Revenue YTD, MTD and This Week
        let ytdNetPostSum = 0;
        (resYtdRev.data?.results || []).forEach(r => {
          ytdNetPostSum += Number(r.net_post) || 0;
        });
        let mtdNetPostSum = 0;
        (resMtdRev.data?.results || []).forEach(r => {
          mtdNetPostSum += Number(r.net_post) || 0;
        });

        const revenueYtd = (ytdNetPostSum / 1000000.0).toFixed(2);
        const revenueMtd = (mtdNetPostSum / 1000000.0).toFixed(2);
        const revenueThisWeek = Number(netRevenuePostDsm || 0).toFixed(2);

        // Schedule Generation Breakup
        const bilatVolNum = Number(bilatVol) || 0;
        const exchangeVolNum = (Number(gdamVol) || 0) + (Number(damVol) || 0) + (Number(rtmVol) || 0);
        const schedTotal = bilatVolNum + exchangeVolNum;
        const bilatPct = schedTotal > 0 ? ((bilatVolNum / schedTotal) * 100).toFixed(1) + '%' : '0.0%';
        const exchangePct = schedTotal > 0 ? ((exchangeVolNum / schedTotal) * 100).toFixed(1) + '%' : '0.0%';
        const bilatVolStr = bilatVolNum.toFixed(2);
        const exchangeVolStr = exchangeVolNum.toFixed(2);

        // Realization (Rs/Kwh)
        // Gross includes Adjustment i.e. receivable - payable
        const schedKwh = Number(totalSched) * 1000000.0;
        const actKwh = Number(totalGen) * 1000000.0;
        const baseKwh = schedKwh > 0 ? schedKwh : (actKwh > 0 ? actKwh : 0);

        let realizationGross = '0.00';
        let realizationCharges = '0.00';
        let realizationDsm = '0.00';
        let realizationPostDsm = '0.00';

        if (baseKwh > 0 && sumGrossRev > 0) {
          const dsmAdj = (Number(dsmReceivable) || 0) - (Number(dsmPayable) || 0);
          const grossRevWithAdj = (Number(totalGrossRev) * 1000000.0) + (dsmAdj * 1000000.0);
          const chargesTotal = (Number(totalGrossRev) - Number(netRevenuePreDsm)) * 1000000.0;
          const dsmPenaltyTotal = (Number(dsmPenaltyVal) || Number(dsmPenalty) || 0) * 1000000.0;

          const rGross = (grossRevWithAdj / baseKwh);
          const rCharges = (chargesTotal / baseKwh);
          const rDsm = (dsmPenaltyTotal / baseKwh);
          const rPostDsm = rGross - rCharges - rDsm;

          realizationGross = rGross.toFixed(2);
          realizationCharges = rCharges.toFixed(2);
          realizationDsm = rDsm.toFixed(2);
          realizationPostDsm = rPostDsm.toFixed(2);
        }

        setSummaryMetrics({
          totalGen,
          totalSched,
          peakGen,
          lowestGen,
          avgGen,
          plf,
          gdamVol,
          damVol,
          rtmVol,
          bilatVol: bilatVolStr,
          bilatPct,
          exchangeVol: exchangeVolStr,
          exchangePct,
          gdamTariff,
          damTariff,
          rtmTariff,
          bilatTariff,
          plantTariff,
          gdamGrossTariff,
          damGrossTariff,
          rtmGrossTariff,
          bilatGrossTariff,
          plantGrossTariff,
          mAvgGdam,
          mAvgDam,
          mAvgRtm,
          totalGrossRev,
          netRevenuePreDsm,
          dsmPenalty,
          netRevenuePostDsm,
          dsmPayable,
          dsmReceivable,
          dsmPenaltyVal,
          netDsmImpact,
          netPlantRealisation,
          revenueYtd,
          revenueMtd,
          revenueThisWeek,
          realizationGross,
          realizationBilatGross: bilatGrossTariff,
          realizationExchGross: exchGrossTariff,
          overallExchRealisation,
          realizationCharges,
          realizationDsm,
          realizationPostDsm,
          efficiency: weeklyEfficiency
        });
      }
    } catch (err) {
      console.error('Error fetching/merging weekly report data:', err);
      setChartData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchWeeklyReportData(startDate, endDate);
  }, [startDate, endDate]);

  // Format date range string: e.g. "22/07/2026 - 28/07/2026"
  const dateFormattedLabel = `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;

    // Gross Realisation daily exchange data for line chart (100% real data)
  const grossRealisationData = useMemo(() => {
    if (chartData && chartData.length > 0) {
      return chartData.map((d, idx) => {
        const val = Number(d.exchange_gross_realisation || 0);
        const idealVal = d.ideal_gross_realisation !== undefined && d.ideal_gross_realisation !== null
          ? Number(d.ideal_gross_realisation)
          : null;
        return {
          date: d.time || (d.period ? dayjs(d.period).format('DD-MMM') : `Day ${idx + 1}`),
          fullDate: d.period,
          value: val,
          idealValue: idealVal
        };
      });
    }

    const data = [];
    const daysDiff = endDate.diff(startDate, 'day');
    for (let i = 0; i <= daysDiff; i++) {
      const cur = startDate.add(i, 'day');
      data.push({
        date: cur.format('DD-MMM'),
        fullDate: cur.format('YYYY-MM-DD'),
        value: 0,
        idealValue: null
      });
    }
    return data;
  }, [chartData, startDate, endDate]);

  const energySegData = useMemo(() => {
    let seg_gt_20 = 0, seg_15_20 = 0, seg_12_15 = 0, seg_safe = 0, seg_neg_12_15 = 0, seg_neg_15_20 = 0, seg_lt_neg_20 = 0;
    let totalScheduleKwh = 0;

    chartData.forEach(d => {
      seg_gt_20 += Number(d.oi_20_plus || 0);
      seg_15_20 += Number(d.oi_15_20 || 0);
      seg_12_15 += Number(d.oi_12_15 || 0);
      seg_safe += (Number(d.oi_0_12 || 0) + Math.abs(Number(d.ui_0_12 || 0)));
      seg_neg_12_15 += Math.abs(Number(d.ui_12_15 || 0));
      seg_neg_15_20 += Math.abs(Number(d.ui_15_20 || 0));
      seg_lt_neg_20 += Math.abs(Number(d.ui_20_plus || 0));

      totalScheduleKwh += Number(d.schedule || 0) * 250.0;
    });

    const val_gt_20 = (seg_gt_20 / 1000.0);
    const val_15_20 = (seg_15_20 / 1000.0);
    const val_12_15 = (seg_12_15 / 1000.0);
    const val_safe = (seg_safe / 1000.0);
    const val_neg_12_15 = (seg_neg_12_15 / 1000.0);
    const val_neg_15_20 = (seg_neg_15_20 / 1000.0);
    const val_lt_neg_20 = (seg_lt_neg_20 / 1000.0);

    return [
      { name: '> 20%', value: val_gt_20, pct: totalScheduleKwh > 0 ? (seg_gt_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '15% to 20%', value: val_15_20, pct: totalScheduleKwh > 0 ? (seg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '12% to 15%', value: val_12_15, pct: totalScheduleKwh > 0 ? (seg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '-12% to 12%', value: val_safe, pct: totalScheduleKwh > 0 ? (seg_safe / totalScheduleKwh * 100) : 0, color: '#27AE60' },
      { name: '-15% to -12%', value: val_neg_12_15, pct: totalScheduleKwh > 0 ? (seg_neg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '-20% to -15%', value: val_neg_15_20, pct: totalScheduleKwh > 0 ? (seg_neg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '< -20%', value: val_lt_neg_20, pct: totalScheduleKwh > 0 ? (seg_lt_neg_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
    ];
  }, [chartData]);

  const totalEnergySeg = useMemo(() => {
    return energySegData.reduce((acc, curr) => acc + curr.value, 0);
  }, [energySegData]);

  const renderCustomBarLabel = (props) => {
    const { x, y, width, height, index } = props;
    const item = energySegData[index];
    if (!item) return null;

    const text = `${item.value.toFixed(2)} (${item.pct.toFixed(2)}%)`;
    const isInside = width > 130;
    const textX = isInside ? x + width - 10 : x + width + 10;
    const textAnchor = isInside ? 'end' : 'start';
    const textColor = isInside ? '#ffffff' : '#333333';

    return (
      <text
        x={textX}
        y={y + height / 2}
        fill={textColor}
        textAnchor={textAnchor}
        dominantBaseline="middle"
        style={{ fontSize: 11, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}
      >
        {text}
      </text>
    );
  };

  const chartMinMax = useMemo(() => {
    if (chartData.length === 0) return null;
    let minActual = Infinity, maxActual = -Infinity, minActualIdx = -1, maxActualIdx = -1;
    let minSchedule = Infinity, maxSchedule = -Infinity, minScheduleIdx = -1, maxScheduleIdx = -1;
    let minRtm = Infinity, maxRtm = -Infinity, minRtmIdx = -1, maxRtmIdx = -1;
    let minGdamPrice = Infinity, maxGdamPrice = -Infinity, minGdamPriceIdx = -1, maxGdamPriceIdx = -1;
    let minGdamCleared = Infinity, maxGdamCleared = -Infinity, minGdamClearedIdx = -1, maxGdamClearedIdx = -1;

    chartData.forEach((row, i) => {
      if (row.actual < minActual) { minActual = row.actual; minActualIdx = i; }
      if (row.actual > maxActual) { maxActual = row.actual; maxActualIdx = i; }
      if (row.schedule < minSchedule) { minSchedule = row.schedule; minScheduleIdx = i; }
      if (row.schedule > maxSchedule) { maxSchedule = row.schedule; maxScheduleIdx = i; }
      if (row.rtm_mcp < minRtm) { minRtm = row.rtm_mcp; minRtmIdx = i; }
      if (row.rtm_mcp > maxRtm) { maxRtm = row.rtm_mcp; maxRtmIdx = i; }
      if (row.gdam_mcp < minGdamPrice) { minGdamPrice = row.gdam_mcp; minGdamPriceIdx = i; }
      if (row.gdam_mcp > maxGdamPrice) { maxGdamPrice = row.gdam_mcp; maxGdamPriceIdx = i; }
      if (row.gdam_cleared < minGdamCleared) { minGdamCleared = row.gdam_cleared; minGdamClearedIdx = i; }
      if (row.gdam_cleared > maxGdamCleared) { maxGdamCleared = row.gdam_cleared; maxGdamClearedIdx = i; }
    });

    return {
      minActualIdx, maxActualIdx, minActual, maxActual,
      minScheduleIdx, maxScheduleIdx, minSchedule, maxSchedule,
      minRtmIdx, maxRtmIdx, minRtm, maxRtm,
      minGdamPriceIdx, maxGdamPriceIdx, minGdamPrice, maxGdamPrice,
      minGdamClearedIdx, maxGdamClearedIdx, minGdamCleared, maxGdamCleared
    };
  }, [chartData]);

  const renderMinMaxLabel = (minIdx, maxIdx, format, registry, color) => (props) => {
    const { x, y, value, index } = props;
    if (index === undefined || value === undefined || value === null) return null;

    const isMin = index === minIdx;
    const isMax = index === maxIdx;

    if (isMin || isMax) {
      const displayValue = format ? format(Number(value)) : value;
      const textStr = String(displayValue);
      const boxWidth = textStr.length * 6 + 8;
      const boxHeight = 12;

      // If the point is too close to the top of the chart, default to rendering below it
      let labelY = (y < 55) ? (y + 12) : (y - 12);

      if (registry) {
        const collisionThresholdX = boxWidth + 10;
        const collisionThresholdY = boxHeight + 4;
        let collisionFound = false;

        for (const pill of registry) {
          const dx = Math.abs(pill.x - x);
          const dy = Math.abs(pill.y - labelY);

          if (dx < collisionThresholdX && dy < collisionThresholdY) {
            collisionFound = true;
            break;
          }
        }

        if (collisionFound) {
          const sameXPills = registry.filter(pill => Math.abs(pill.x - x) < collisionThresholdX);
          if (y < 55) {
            // Near the top: stack downwards to avoid going out of bounds
            const maxY = Math.max(...sameXPills.map(p => p.y));
            labelY = maxY + collisionThresholdY;
          } else {
            // Further down: stack upwards
            const minY = Math.min(...sameXPills.map(p => p.y));
            labelY = minY - collisionThresholdY;
          }
        }
        registry.push({ x, y: labelY });
      }

      const isAbove = labelY < y;
      const pillEdgeY = isAbove ? (labelY + boxHeight / 2) : (labelY - boxHeight / 2);

      return (
        <g>
          {/* Vertical dotted indicator line of the same color as the curve */}
          <line
            x1={x}
            y1={y}
            x2={x}
            y2={pillEdgeY}
            stroke={color || '#1e293b'}
            strokeWidth={1}
            strokeDasharray="2 2"
          />
          <circle cx={x} cy={y} r={3} fill="#1e293b" />
          <rect
            x={x - boxWidth / 2}
            y={labelY - boxHeight / 2}
            width={boxWidth}
            height={boxHeight}
            fill="#ffffff"
            stroke="#cbd5e1"
            strokeWidth={1}
            rx={2}
          />
          <text
            x={x}
            y={labelY + 1}
            fill="#1e293b"
            fontSize={8}
            fontWeight={700}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {displayValue}
          </text>
        </g>
      );
    }
    return null;
  };


  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
    setGenerationTime(now);

    await new Promise((r) => setTimeout(r, 600));

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageIds = ['pdf-page-1'];

      for (let i = 0; i < pageIds.length; i++) {
        const pageEl = document.getElementById(pageIds[i]);
        if (!pageEl) continue;

        const canvas = await html2canvas(pageEl, {
          scale: 2.2,
          useCORS: true,
          logging: false,
          scrollY: 0,
          scrollX: 0,
        });

        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        if (i < pageIds.length - 1) {
          pdf.addPage();
        }
      }

      pdf.save(`Apraava_Weekly_Report_${startDate.format('YYYY_MM_DD')}_to_${endDate.format('YYYY_MM_DD')}.pdf`);
    } catch (err) {
      console.error('Weekly Report PDF Generation failed:', err);
    }
    setGeneratingPdf(false);
  };

  const handleDownloadWord = async () => {
    setGeneratingWord(true);
    try {
      // Pause slightly to ensure charts are fully settled
      await new Promise((r) => setTimeout(r, 450));

      const images = {};
      const ids = {
        chart2: 'weekly-chart-section-2',
        chart3: 'weekly-chart-section-3',
        chart4: 'weekly-chart-section-4',
        chart5: 'weekly-chart-section-5',
        chart6: 'weekly-chart-section-6',
        chart7: 'weekly-chart-section-7',
      };

      for (const [key, id] of Object.entries(ids)) {
        const el = document.getElementById(id);
        if (el) {
          try {
            const canvas = await html2canvas(el, {
              scale: 2.0,
              useCORS: true,
              logging: false,
            });
            images[key] = canvas.toDataURL('image/png');
          } catch (canvasErr) {
            console.error(`Failed to capture weekly chart element #${id}:`, canvasErr);
          }
        }
      }

      await exportWeeklyReportDocx(
        dateFormattedLabel,
        summaryMetrics,
        chartData,
        marketSnapshotData,
        energySegData,
        images
      );
    } catch (err) {
      console.error('Weekly Report Word Generation failed:', err);
    }
    setGeneratingWord(false);
  };

  const ReportHeader = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `2px solid ${COLORS.primary}`,
      paddingBottom: 6,
      marginBottom: 12,
      width: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img src={logoImg} alt="Apraava Logo" style={{ height: 24, width: 'auto' }} />
      </div>
    </div>
  );

  const chart1Registry = [];
  const chart2Registry = [];
  const chart3Registry = [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', background: '#f1f5f9', height: '100vh', fontFamily: 'Inter, sans-serif' }}>

      {/* CSS Styles for PDF Document Preview */}
      <style>{`
        .a4-page-container {
          box-shadow: 0 4px 15px rgba(0,0,0,0.06);
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          background-color: #ffffff;
          width: 794px; /* Exact A4 dimensions at 96dpi */
          height: 1123px;
          padding: 40px 50px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          position: relative;
          margin-bottom: 24px;
          overflow: hidden;
        }
        .report-section {
          margin-bottom: 12px;
        }
        .report-section-title {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          padding-bottom: 3px;
          margin-bottom: 20px;
          letter-spacing: 0.5px;
        }
        .report-table-container {
          margin: 4px 0 12px 0;
          border: 1px solid ${COLORS.border};
          border-radius: 4px;
          overflow: hidden;
        }
        .report-table-container table {
          border-collapse: collapse;
          width: 100%;
          font-size: 12px;
        }
        .report-table-container th {
          background-color: #f8fafc;
          border-bottom: 1px solid ${COLORS.border};
          color: #334155;
          font-weight: 700;
          padding: 6px 10px;
          text-align: left;
        }
        .report-table-container td {
          border-bottom: 1px dashed #e2e8f0;
          padding: 6px 10px;
          color: #334155;
        }
        .report-table-container tr:last-child td {
          border-bottom: none;
        }
        .report-table-container tr:nth-child(even) {
          background-color: #fafafa;
        }
        .pdf-page-title-block {
          margin: 15px 0 20px 0;
          border-left: 6px solid ${COLORS.primary};
          padding-left: 16px;
        }
      `}</style>

      {/* TOP RIBBON: Date Selection & PDF Download Trigger */}
      <div style={{
        height: '60px',
        minHeight: '60px',
        background: '#ffffff',
        borderBottom: '1px solid #cbd5e1',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <Space align="center" size={12}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>Start Date:</span>
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            format="YYYY-MM-DD"
            disabledDate={(current) => current && current.isAfter(dayjs().subtract(1, 'day'), 'day')}
            allowClear={false}
            style={{ width: 130 }}
          />
          <span style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>End Date:</span>
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            format="YYYY-MM-DD"
            disabledDate={(current) => {
              if (!current) return false;
              const yesterday = dayjs().subtract(1, 'day');
              const beforeStart = current.isBefore(startDate, 'day');
              const moreThanWeek = current.isAfter(startDate.add(6, 'day'), 'day');
              const afterYesterday = current.isAfter(yesterday, 'day');
              return beforeStart || moreThanWeek || afterYesterday;
            }}
            allowClear={false}
            style={{ width: 130 }}
          />
          {(!startDate.isSame(defaultStartDate, 'day') || !endDate.isSame(defaultEndDate, 'day')) && (
            <Button
              type="default"
              onClick={() => {
                setStartDate(defaultStartDate);
                setEndDate(defaultEndDate);
              }}
              style={{ fontWeight: 600, color: COLORS.primary, borderColor: COLORS.primary }}
            >
              Reset
            </Button>
          )}
        </Space>

        <Space>
          <Button
            type="primary"
            icon={<FilePdfOutlined />}
            loading={generatingPdf}
            disabled={loading}
            onClick={handleDownloadPdf}
            style={{ background: '#E74C3C', borderColor: '#E74C3C', fontWeight: 600 }}
            title="Download PDF Report"
          >
            PDF
          </Button>
          {/* <Button
            type="primary"
            icon={<FileWordOutlined />}
            loading={generatingWord}
            disabled={loading}
            onClick={handleDownloadWord}
            style={{ background: '#2B579A', borderColor: '#2B579A', fontWeight: 600 }}
            title="Download Word Report"
          >
            Word
          </Button> */}
        </Space>
      </div>

      {/* PREVIEW CONTENT: Centered scrollable preview of PDF pages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '30px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        {loading ? (
          <div style={{ marginTop: 200, textAlign: 'center' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div id="weekly-report-printable-area" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

            {/* ==================== PAGE 1 ==================== */}
            <div id="pdf-page-1" className="a4-page-container">
              <div>
                <ReportHeader />

                {/* Page Title Block */}
                <div className="pdf-page-title-block">
                  <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.primary, lineHeight: 1.1, margin: '4px 0' }}>
                    Weekly Performance Report
                  </h1>
                  <h2 style={{ fontSize: 13, fontWeight: 500, color: '#64748b', margin: 0 }}>
                    {dateFormattedLabel}
                  </h2>
                </div>

                {/* Section 1: Summary Overview */}
                <div className="report-section" style={{ marginTop: 16 }}>
                  <div className="report-section-title" style={{ fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14, color: '#0f172a' }}>
                    1. SUMMARY OVERVIEW
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', marginBottom: '16px' }}>
                    {/* Card 1: GENERATION OVERVIEW */}
                    <div style={{
                      border: '1.5px solid #cbd5e1',
                      borderRadius: '12px',
                      background: '#ffffff',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                      <div style={{
                        background: '#f1f5f9',
                        padding: '9px 16px',
                        borderBottom: '1px solid #cbd5e1',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '0.5px'
                      }}>
                        GENERATION OVERVIEW
                      </div>
                      <div style={{ display: 'flex', padding: '14px 18px', alignItems: 'center', minHeight: '80px', boxSizing: 'border-box' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Actual Generation</div>
                          <div style={{ display: 'flex', alignItems: 'baseline' }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.totalGen}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginLeft: '4px' }}>MUs</span>
                          </div>
                        </div>
                        <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', margin: '0 16px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>PLF</div>
                          <div>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                              {summaryMetrics.plf !== '—' && summaryMetrics.plf !== null ? `${summaryMetrics.plf}%` : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card 2: Sold(scheduled) Unit breakup */}
                    <div style={{
                      border: '1.5px solid #cbd5e1',
                      borderRadius: '12px',
                      background: '#ffffff',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                      <div style={{
                        background: '#f1f5f9',
                        padding: '9px 16px',
                        borderBottom: '1px solid #cbd5e1',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '0.5px'
                      }}>
                        Sold(scheduled) Unit breakup
                      </div>
                      <div style={{ display: 'flex', padding: '14px 18px', alignItems: 'center', minHeight: '80px', boxSizing: 'border-box' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Bilateral</div>
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.bilatVol}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>MUs</span>
                            <span style={{
                              background: '#e2e8f0',
                              borderRadius: '12px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: 700,
                              color: '#1e293b'
                            }}>
                              {summaryMetrics.bilatPct}
                            </span>
                          </div>
                        </div>
                        <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', margin: '0 16px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Exchange</div>
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.exchangeVol}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>MUs</span>
                            <span style={{
                              background: '#e2e8f0',
                              borderRadius: '12px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: 700,
                              color: '#1e293b'
                            }}>
                              {summaryMetrics.exchangePct}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card 3: REVENUE */}
                    <div style={{
                      border: '1.5px solid #cbd5e1',
                      borderRadius: '12px',
                      background: '#ffffff',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        background: '#f1f5f9',
                        padding: '9px 16px',
                        borderBottom: '1px solid #cbd5e1',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '0.5px'
                      }}>
                        REVENUE
                      </div>
                      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, justifyContent: 'center' }}>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          padding: '7px 14px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          border: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>YTD</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>₹ {summaryMetrics.revenueYtd} M</span>
                        </div>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          padding: '7px 14px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          border: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>MTD</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>₹ {summaryMetrics.revenueMtd} M</span>
                        </div>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          padding: '7px 14px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          border: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>This Week</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>₹ {summaryMetrics.revenueThisWeek} M</span>
                        </div>
                      </div>
                    </div>

                    {/* Card 4: PLANT REALIZATION (Rs/Kwh) */}
                    <div style={{
                      border: '1.5px solid #cbd5e1',
                      borderRadius: '12px',
                      background: '#ffffff',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        background: '#f1f5f9',
                        padding: '9px 16px',
                        borderBottom: '1px solid #cbd5e1',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '0.5px'
                      }}>
                        PLANT REALIZATION (Rs/Kwh)
                      </div>
                      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, justifyContent: 'center' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px solid #f1f5f9'
                        }}>
                          <div>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>Gross</span>
                            <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '6px', fontWeight: 500 }}>
                              (Bilat: {summaryMetrics.realizationBilatGross && summaryMetrics.realizationBilatGross !== '—' ? summaryMetrics.realizationBilatGross : '—'} | Exch: {summaryMetrics.realizationExchGross && summaryMetrics.realizationExchGross !== '—' ? summaryMetrics.realizationExchGross : '—'})
                            </span>
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.realizationGross}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Charges & Commission</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                            {summaryMetrics.realizationCharges && summaryMetrics.realizationCharges !== '—'
                              ? `- ${String(summaryMetrics.realizationCharges).replace(/^-/, '').trim()}`
                              : '—'}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px solid #f1f5f9'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>DSM Penalty</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                            {summaryMetrics.realizationDsm && summaryMetrics.realizationDsm !== '—'
                              ? `- ${String(summaryMetrics.realizationDsm).replace(/^-/, '').trim()}`
                              : '—'}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Revenue Post DSM</span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{summaryMetrics.realizationPostDsm}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Gross Realisation Graph */}
                <div className="report-section" style={{ marginTop: 52 }}>
                  {/* Title & Overall Realisation Header with horizontal line */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    borderBottom: '1.5px solid #cbd5e1',
                    paddingBottom: 6,
                    marginBottom: 16
                  }}>
                    <div style={{
                      fontSize: 15,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: COLORS.primary
                    }}>
                      Gross Realisation (Exchange)
                    </div>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#0f172a'
                    }}>
                      <span style={{ fontWeight: 600, color: '#64748b', marginRight: 6 }}>Overall Realisation:</span>
                      <span style={{ color: COLORS.primary, fontWeight: 800 }}>
                        {summaryMetrics.overallExchRealisation && summaryMetrics.overallExchRealisation !== '—'
                          ? `₹ ${summaryMetrics.overallExchRealisation} / kWh`
                          : '—'}
                      </span>
                    </div>
                  </div>

                  {/* Clean unboxed chart matching report theme */}
                  <div id="weekly-chart-gross-realisation" style={{ width: '100%', height: 260, margin: '8px 0' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={grossRealisationData}
                        margin={{ top: 22, right: 15, left: 20, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10, fill: '#64748b' }}
                          stroke="#64748b"
                          tickLine={{ stroke: '#64748b' }}
                        />
                        <YAxis
                          width={48}
                          tick={{ fontSize: 10, fill: '#64748b' }}
                          stroke="#64748b"
                          tickLine={{ stroke: '#64748b' }}
                          domain={[0, (dataMax) => Math.ceil((Number(dataMax) || 4) * 1.25)]}
                          label={{
                            value: 'Tariff Realization (₹/kWh)',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 0,
                            dy: 20,
                            style: { textAnchor: 'middle', fill: '#64748b', fontSize: 10, fontWeight: 500 }
                          }}
                        />
                        <Tooltip
                          formatter={(value, name) => [
                            value !== null && value !== undefined ? `₹ ${Number(value).toFixed(2)} / kWh` : '—',
                            name
                          ]}
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            opacity: 1,
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            fontSize: '11px'
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
                        />
                        <Line
                          isAnimationActive={false}
                          type="monotone"
                          dataKey="value"
                          name="Gross Realisation (Exchange)"
                          stroke={COLORS.primary}
                          strokeWidth={2.5}
                          dot={{ fill: COLORS.primary, stroke: '#ffffff', strokeWidth: 1.5, r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          isAnimationActive={false}
                          type="monotone"
                          dataKey="idealValue"
                          name="Ideal Scenario (Electricity)"
                          stroke="#10B981"
                          strokeWidth={2.5}
                          dot={{ fill: '#10B981', stroke: '#ffffff', strokeWidth: 1.5, r: 4 }}
                          activeDot={{ r: 6 }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Efficiency KPI (relative to ideal) */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginTop: 4,
                    paddingRight: 15
                  }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#1E293B' }}>
                        Efficiency:{' '}
                        <span style={{ color: COLORS.primary, fontWeight: 700, fontSize: '15px' }}>
                          {summaryMetrics.efficiency && summaryMetrics.efficiency !== '—'
                            ? `${summaryMetrics.efficiency}%`
                            : '—'}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748B', fontWeight: 500, marginTop: '-2px' }}>
                        (relative to ideal)
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        )}
      </div>

    </div>
  );
}
